// Transaction Service - GraphQL operations for Transactions
import { graphqlClient } from '../graphqlClient';

const TRANSACTION_FIELDS = `
  id
  provider_transaction_id
  type
  status
  amount
  fee_amount
  net_amount
  currency
  country
  payment_method
  provider_id
  provider_code
  processor_bankcode
  payout_type
  platformName
  ip_address
  customer_name
  customer_email
  customer_phone
  merchant_id
  merchant_code
  merchant_name
  reseller_id
  reseller_code
  reseller_name
  engine_id
  engine_code
  engine_name
  document_id
  firstname
  lastname
  city
  address
  state
  postcode
  hash_verified
  callback_status
  callback_attempts
  callback_received_at
  callback_response
  expires_at
  processed_at
  completed_at
  api_response
  error
  error_message
  metadata
  provider {
    id
    code
    name
    nameEn
    status
    createdAt
    updatedAt
  }
  createdAt
  updatedAt
`;

// ==================== Admin Transactions ====================

export const getAllTransactions = async () => {
  const query = `
    query GetAllTransactions {
      getAllTransactions {
        ${TRANSACTION_FIELDS}
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllTransactions || [];
};

export const getPaginatedTransactions = async (page = 1, limit = 10, search = "", filters = {}, merchantIds = []) => {
  const query = `
    query GetPaginatedTransactions($page: Int, $limit: Int, $search: String, $filters: TransactionPaginationFilterInput, $merchants: [String]) {
      merchantTransactionsPaginated(page: $page, limit: $limit, search: $search, filters: $filters, merchants: $merchants) {
        data {
          ${TRANSACTION_FIELDS}
        }
        total
        totalPages
        page
        limit
      }
    }
  `;

  const result = await graphqlClient.query(query, {
    page,
    limit,
    search: search || null,
    filters: Object.keys(filters).length > 0 ? filters : null,
    merchants: merchantIds && merchantIds.length > 0 ? merchantIds : null
  });

  return result.merchantTransactionsPaginated || { data: [], total: 0, totalPages: 0, page: 1, limit: 10 };
};

// ==================== Merchant Transactions ====================

export const getMerchantTransactions = async () => {
  const query = `
    query GetMerchantTransactions {
      merchantTransactions {
        ${TRANSACTION_FIELDS}
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.merchantTransactions || [];
};

// ==================== Reseller Transactions ====================

export const getResellerTransactions = async () => {
  const query = `
    query GetResellerTransactions {
      resellerTransactions {
        ${TRANSACTION_FIELDS}
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.resellerTransactions || [];
};

// ==================== Engine Transactions ====================

export const getEngineTransactions = async () => {
  const query = `
    query GetEngineTransactions {
      engineTransactions {
        ${TRANSACTION_FIELDS}
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.engineTransactions || [];
};

// ==================== Get Transaction By ID ====================

export const getTransactionById = async (id) => {
  const query = `
    query GetTransactionById($id: String!) {
      transactionById(id: $id) {
        ${TRANSACTION_FIELDS}
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.transactionById;
};

// ==================== Unique Provider Names ====================

export const getUniqueProviders = async () => {
  const query = `
    query GetAllTransactionProviders {
      allTransactionProviders
    }
  `;

  const result = await graphqlClient.query(query);
  return result.allTransactionProviders || [];
};

// ==================== Update Transaction Status (Admin Only) ====================

export const updateTransactionStatus = async (id, status, adminNote) => {
  const mutation = `
    mutation UpdateTransactionStatus($id: String!, $input: UpdateTransactionStatusInput!) {
      updateTransactionStatus(id: $id, input: $input) {
        ${TRANSACTION_FIELDS}
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, {
    id,
    input: {
      status,
      admin_note: adminNote || null,
    },
  });
  return result.updateTransactionStatus;
};
